/**
 * MetricChart — wrapper React do uPlot.
 *
 * Features:
 *   - Sync de cursor entre múltiplos charts (todos no mesmo cursor group)
 *   - Dark mode automático (lê do <html class="dark">)
 *   - Door events viram faixas verticais ambar (plugin hook 'drawAxes')
 *   - Responsivo (auto-resize via ResizeObserver)
 *   - Tooltip via legend nativo do uPlot
 *
 * Pra uPlot funcionar com prop reativa, recriamos a instância quando
 * series/data mudam (uPlot não tem API setData partial).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import uPlot from "uplot";
import "uplot/dist/uPlot.min.css";
import { useTheme } from "@/contexts/ThemeContext";

export interface ChartSeries {
  label: string;
  /** Valores Y alinhados com `timestamps`. Pode ter null pra gaps. */
  values: (number | null)[];
  /** Cor da linha. */
  color: string;
  /** Largura do stroke (default 2). */
  width?: number;
  /** Eixo Y: 0 (esquerdo) ou 1 (direito). Default 0. */
  axis?: 0 | 1;
  /** Unidade de medida — usada pra labels dos eixos no painel custom. */
  unit?: string;
  /**
   * Padrão dash — array de segmentos em px (ex.: [6, 4] = 6 sólido, 4 vazio).
   * Usado em séries de comparação (dia anterior / 7 dias atrás) pra
   * distinguir visualmente do período atual sem confundir a leitura. #174
   */
  dash?: number[];
}

export interface DoorEvent {
  /** Início da abertura (timestamp em segundos epoch). */
  openedAt: number;
  /** Fim da abertura. Se null/undefined, considera "ainda aberta". */
  closedAt?: number | null;
}

interface Props {
  /** Timestamps em segundos epoch, ordem crescente. */
  timestamps: number[];
  /** Séries plotadas. */
  series: ChartSeries[];
  /** Eventos de porta aberta → renderiza faixas sombreadas. */
  doorEvents?: DoorEvent[];
  /** Grupo pra sync de cursor entre charts. Default "default". */
  syncKey?: string;
  /** Altura em pixels. Default 280. */
  height?: number;
  /** Label do eixo Y esquerdo. */
  yLabel?: string;
  /** Label do eixo Y direito (se houver série com axis=1). */
  yLabel2?: string;
  /**
   * Intervalo máximo, em segundos, que ainda vale ligar com linha contínua.
   * Acima disso a ligação vira tracejada. Default 600 (10 min).
   */
  gapMaxSec?: number;
  className?: string;
}

const PALETTE_DARK = {
  bg:    "transparent",
  text:  "#94a3b8",
  grid:  "rgba(148,163,184,0.10)",
  axis:  "rgba(148,163,184,0.20)",
  door:  "rgba(245,158,11,0.18)",
  cursor:"rgba(34,211,238,0.65)",
};
const PALETTE_LIGHT = {
  bg:    "transparent",
  text:  "#64748b",
  grid:  "rgba(100,116,139,0.10)",
  axis:  "rgba(100,116,139,0.25)",
  door:  "rgba(245,158,11,0.18)",
  cursor:"rgba(8,145,178,0.65)",
};

/** Plugin uPlot que desenha faixas verticais (door events) no fundo. */
function doorOverlayPlugin(getEvents: () => DoorEvent[], color: string) {
  return {
    hooks: {
      drawClear: (u: uPlot) => {
        const ctx = u.ctx;
        const events = getEvents();
        if (!events.length) return;
        const xMin = u.scales.x.min;
        const xMax = u.scales.x.max;
        if (xMin == null || xMax == null) return;

        const top    = u.bbox.top;
        const height = u.bbox.height;

        ctx.save();
        ctx.fillStyle = color;
        for (const ev of events) {
          const endTs = ev.closedAt ?? xMax;
          // Skip eventos completamente fora da viewport
          if (endTs < xMin || ev.openedAt > xMax) continue;
          const x1 = u.valToPos(Math.max(ev.openedAt, xMin), "x", true);
          const x2 = u.valToPos(Math.min(endTs, xMax),    "x", true);
          ctx.fillRect(x1, top, Math.max(1, x2 - x1), height);
        }
        ctx.restore();
      },
    },
  };
}

/**
 * Vãos longos viram linha TRACEJADA, não contínua.
 *
 * Uma linha contínua ligando duas leituras separadas por seis horas é o
 * gráfico afirmando um dado que não existe: quem olha lê "a temperatura subiu
 * devagar a tarde toda" quando o que houve foi silêncio. É o mesmo erro que o
 * motor de alertas comete quando trata ausência de dado como ausência de
 * problema — aqui em forma de pixel, e sem ninguém pra conferir depois.
 *
 * Por que tracejado e não cortar a linha: o corte não distingue "sem dado" de
 * "fim da série", e gráfico picotado parece defeito de renderização — o
 * usuário atribui ao software, não ao sensor. O tracejado diz a coisa certa:
 * existe ligação entre os dois pontos, mas ela é inferida, não medida.
 *
 * Por que não `spanGaps: false`: as séries são alinhadas na UNIÃO dos
 * timestamps de todas as métricas, então cada série já tem `null` onde só as
 * outras reportaram. Desligar spanGaps estilhaçaria toda linha por causa de
 * defasagens de segundos entre sensores. O critério tem que ser TEMPO
 * DECORRIDO entre leituras válidas, não presença de null.
 */
const VAO_CONTINUO_SEG = 600;

/** Traço curto e espaçado: lê-se como "inferido", e não se confunde com o
 *  dash [6,4] das séries de comparação do #174. */
const DASH_INFERIDO = [2, 5];

/**
 * O construtor de paths devolve só o traço sólido; as pontes tracejadas
 * ficam neste estoque pro plugin desenhar depois.
 *
 * Precisa ser em dois tempos porque o dash é estado do CONTEXTO do canvas,
 * não do Path2D — um único path não consegue ter dois padrões de traço. O
 * uPlot chama o construtor e depois o hook `drawSeries` da mesma série, nessa
 * ordem, então o estoque sempre está preenchido na hora da leitura.
 */
type EstoqueTracejado = Map<number, Path2D>;

function construirPaths(limiarSeg: number, estoque: EstoqueTracejado) {
  return (u: uPlot, sidx: number, i0: number, i1: number) => {
    const xs = u.data[0] as number[];
    const ys = u.data[sidx] as (number | null)[];
    const escalaY = (u.series[sidx] as { scale?: string }).scale ?? "y";

    const solido = new Path2D();
    const tracejado = new Path2D();
    let temTracejado = false;
    let ant = -1;

    for (let i = i0; i <= i1; i++) {
      const y = ys[i];
      if (y == null || !Number.isFinite(y)) continue;

      const px = u.valToPos(xs[i], "x", true);
      const py = u.valToPos(y, escalaY, true);

      if (ant < 0) {
        solido.moveTo(px, py);
        ant = i;
        continue;
      }

      if (xs[i] - xs[ant] > limiarSeg) {
        tracejado.moveTo(
          u.valToPos(xs[ant], "x", true),
          u.valToPos(ys[ant] as number, escalaY, true),
        );
        tracejado.lineTo(px, py);
        temTracejado = true;
        // Recomeça o sólido no ponto novo: o trecho do vão já foi desenhado
        // pela ponte, e continuar o path aqui pintaria os dois por cima.
        solido.moveTo(px, py);
      } else {
        solido.lineTo(px, py);
      }
      ant = i;
    }

    if (temTracejado) estoque.set(sidx, tracejado);
    else estoque.delete(sidx);

    return { stroke: solido };
  };
}

/** Desenha as pontes inferidas na cor da própria série, com dash próprio. */
function tracejadoPlugin(estoque: EstoqueTracejado) {
  return {
    hooks: {
      drawSeries: (u: uPlot, sidx: number) => {
        const caminho = estoque.get(sidx);
        if (!caminho) return;

        const s = u.series[sidx] as { stroke?: unknown; width?: number };
        const cor =
          typeof s.stroke === "function"
            ? (s.stroke as (self: uPlot, i: number) => string)(u, sidx)
            : (s.stroke as string);
        const ratio =
          (uPlot as unknown as { pxRatio?: number }).pxRatio ??
          window.devicePixelRatio ??
          1;

        const ctx = u.ctx;
        ctx.save();
        // Sem o clip, a ponte vaza por cima dos eixos quando o zoom corta um
        // dos extremos do vão.
        ctx.beginPath();
        ctx.rect(u.bbox.left, u.bbox.top, u.bbox.width, u.bbox.height);
        ctx.clip();
        ctx.strokeStyle = cor;
        ctx.lineWidth = (s.width ?? 2) * ratio;
        ctx.setLineDash(DASH_INFERIDO.map((n) => n * ratio));
        ctx.stroke(caminho);
        ctx.restore();
      },
    },
  };
}

export function MetricChart({
  timestamps,
  series,
  doorEvents = [],
  syncKey = "default",
  height = 280,
  yLabel,
  yLabel2,
  gapMaxSec = VAO_CONTINUO_SEG,
  className,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const uplotRef = useRef<uPlot | null>(null);
  const doorRef = useRef<DoorEvent[]>(doorEvents);
  doorRef.current = doorEvents;
  // ID estável por INSTÂNCIA (não compartilha com syncKey, que é o mesmo
  // entre painéis do mesmo equipment). Serve só pra logs.
  const instanceIdRef = useRef(Math.random().toString(36).slice(2, 7));

  // Contador de "force recreate" — bumped quando algum erro do uPlot exige
  // destruir e criar do zero. Entra no structureKey pra triggerar Effect 1.
  const [recreateNonce, setRecreateNonce] = useState(0);
  const forceRecreate = useCallback((reason: string) => {
    console.warn(`[uPlot] force recreate: ${reason}`);
    if (uplotRef.current) {
      try { uplotRef.current.destroy(); } catch { /* ignore */ }
      uplotRef.current = null;
    }
    setRecreateNonce((n) => n + 1);
  }, []);
  // Refs que o useEffect lê — atualizar via ref evita re-criar o uPlot
  // a cada novo array de timestamps/series (que vem de refetch periódico
  // do TanStack mesmo quando o CONTEÚDO é igual).
  const seriesRef = useRef(series);
  seriesRef.current = series;
  const timestampsRef = useRef(timestamps);
  timestampsRef.current = timestamps;

  const { resolved } = useTheme();
  const palette = resolved === "dark" ? PALETTE_DARK : PALETTE_LIGHT;

  // "Structure key" — só muda quando algo que exige RECRIAR o uPlot mudou:
  //   - número de séries, label/cor/eixo/largura de cada uma
  //   - syncKey, height, yLabel, yLabel2
  //   - tema (palette muda)
  //
  // Mudança de valores das séries OU de timestamps NÃO incrementa essa key —
  // esses casos vão por setData() no efeito #2 sem destruir o uPlot.
  // Sem essa separação, o gráfico SUMIA a cada 10s (refetch das readings)
  // porque o useEffect destruía e tentava recriar; se o recreate caísse
  // num timing ruim de layout, falhava silenciosamente.
  const structureKey = useMemo(() => {
    return JSON.stringify({
      n: series.length,
      cfg: series.map((s) => ({ l: s.label, c: s.color, w: s.width, a: s.axis ?? 0 })),
      syncKey, height, yLabel, yLabel2, gapMaxSec,
      theme: resolved,
      r: recreateNonce,
    });
  }, [series, syncKey, height, yLabel, yLabel2, gapMaxSec, resolved, recreateNonce]);

  // Effect 1: cria/destrói uPlot — roda só quando ESTRUTURA muda.
  // ResizeObserver é a fonte de verdade pra primeira criação.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let cancelled = false;
    // Um estoque por instância de uPlot — duas instâncias na mesma página
    // compartilhando o Map se sobrescreveriam pelo índice da série.
    const estoqueTracejado: EstoqueTracejado = new Map();

    function buildOpts(width: number): uPlot.Options {
      const currentSeries = seriesRef.current;
      // Detecta se ALGUMA série usa o eixo Y direito (axis=1). Necessário
      // pra criar o axis y2 — se não criar e tiver série em scale "y2",
      // uPlot quebra com "object null is not iterable" em axis._found.
      const hasRightAxis = currentSeries.some((s) => s.axis === 1) || !!yLabel2;
      return {
        width,
        height,
        cursor: {
          // Cursor sincronizado entre painéis com mesmo syncKey.
          // Era a causa raiz do axis._found null no início, mas agora temos
          // 3 camadas de defesa que cobrem o caso:
          //   1. ChartPanel filtra series=[] antes de montar MetricChart
          //   2. y2 axis criado condicionalmente (não declara se não usa)
          //   3. Error listener global auto-recriar se cair em race
          // Sync no eixo X (tempo) — o Y de cada chart tem unidades diferentes
          // e não faz sentido sincronizar.
          sync: { key: syncKey, scales: ["x", null] },
          focus: { prox: 24 },
          points: { size: 7 },
        },
        legend: { live: true },
        scales: {
          x: { time: true },
          y:  { auto: true },
          // y2 só declarado se há série/label usando — declarar uma scale
          // sem nada usando também causa axis._found null em algumas versões
          ...(hasRightAxis ? { y2: { auto: true } } : {}),
        },
        axes: [
          {
            stroke: palette.text,
            grid:   { stroke: palette.grid, width: 1 },
            ticks:  { stroke: palette.axis, width: 1 },
            font:   "11px 'JetBrains Mono', monospace",
          },
          {
            scale: "y",
            stroke: palette.text,
            grid:   { stroke: palette.grid, width: 1 },
            ticks:  { stroke: palette.axis, width: 1 },
            font:   "11px 'JetBrains Mono', monospace",
            label:  yLabel,
            labelFont: "11px 'JetBrains Mono', monospace",
          },
          ...(hasRightAxis
            ? [
                {
                  scale: "y2",
                  side: 1 as const,
                  stroke: palette.text,
                  grid:   { show: false },
                  ticks:  { stroke: palette.axis, width: 1 },
                  font:   "11px 'JetBrains Mono', monospace",
                  label:  yLabel2,
                  labelFont: "11px 'JetBrains Mono', monospace",
                },
              ]
            : []),
        ],
        series: [
          { label: "Tempo" },
          ...currentSeries.map((s) => ({
            label:  s.label,
            stroke: s.color,
            width:  s.width ?? 2,
            // #174 séries de comparação: linha tracejada pra distinguir
            // visualmente do período atual (mesmo com cor similar/opaca).
            ...(s.dash ? { dash: s.dash } : {}),
            // Se não criamos y2 axis, força tudo no y (fallback defensivo
            // pra nunca apontar pra scale inexistente).
            scale:  hasRightAxis && s.axis === 1 ? "y2" : "y",
            // `paths` decide o que é contínuo e o que é ponte tracejada; o
            // spanGaps deixa de importar, mas fica true pra que qualquer
            // caminho de fallback do uPlot não estilhace a linha por causa
            // dos nulls da união de timestamps.
            spanGaps: true,
            paths: construirPaths(gapMaxSec, estoqueTracejado),
            points: { show: false },
          })),
        ],
        plugins: [
          doorOverlayPlugin(() => doorRef.current, palette.door),
          tracejadoPlugin(estoqueTracejado),
        ],
      };
    }

    function dataMatrix(): uPlot.AlignedData {
      return [
        timestampsRef.current,
        ...seriesRef.current.map((s) => s.values as (number | null)[]),
      ];
    }

    // Identificador único pra distinguir instâncias nos logs (vários
    // ChartPanel = vários MetricChart = N uPlot por página).
    const id = `${syncKey || "?"}-${instanceIdRef.current}-${yLabel ?? ""}`;

    function createOrResize(width: number, source: string) {
      if (cancelled || !el || width <= 0) {
        console.debug(`[uPlot/${id}] skip: ${source} w=${width} cancelled=${cancelled}`);
        return;
      }
      // GUARDA CRÍTICA: uPlot quebra com axis._found null se criado com
      // 0 séries de dados (só o eixo X). É inválido conceitualmente —
      // chart sem nada pra plotar. Aborta a criação aqui mesmo.
      // O ChartPanel já filtra isso, mas duplicamos pra defender contra
      // race conditions e versões antigas em cache.
      if (seriesRef.current.length === 0) {
        console.debug(`[uPlot/${id}] skip: ${source} — sem séries (0 data series)`);
        return;
      }
      if (timestampsRef.current.length === 0) {
        console.debug(`[uPlot/${id}] skip: ${source} — sem timestamps`);
        return;
      }

      if (uplotRef.current) {
        // Já existe — só ajusta tamanho (uPlot redesenha internamente)
        uplotRef.current.setSize({ width, height });
        return;
      }
      // Primeira vez com tamanho válido — cria a instância
      try {
        uplotRef.current = new uPlot(buildOpts(width), dataMatrix(), el);
        console.debug(
          `[uPlot/${id}] CREATED via ${source} w=${width} series=${seriesRef.current.length} ` +
          `axes=${seriesRef.current.some((s) => s.axis === 1) ? "y+y2" : "y"}`,
        );
      } catch (err) {
        console.error(`[uPlot/${id}] FALHA ao criar via ${source}:`, err);
        console.error(`[uPlot/${id}] series:`, seriesRef.current);
        console.error(`[uPlot/${id}] timestamps len:`, timestampsRef.current.length);
      }
    }

    // Tenta criar imediatamente se já houver tamanho (pega o caso comum)
    if (el.clientWidth > 0) {
      createOrResize(el.clientWidth, "immediate");
    } else {
      console.debug(`[uPlot/${id}] container w=0 no mount, esperando RO/fallback`);
    }

    // ResizeObserver pega TODAS as outras situações:
    //   - container começou com 0×0 e ganhou tamanho depois
    //   - viewport mudou de tamanho (responsivo)
    //   - layout flex/grid recalculou
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.round(entry.contentRect.width);
        createOrResize(w, "resizeobserver");
      }
    });
    ro.observe(el);

    // FALLBACK: se em 250ms o uPlot ainda não foi criado (RO não disparou
    // ou disparou com w=0), força criação usando largura do parent ou um
    // default. Em alguns browsers o RO simplesmente não dispara quando o
    // container é montado já com tamanho final (não houve "resize" — sempre
    // foi aquele tamanho). Esse fallback cobre exatamente esse caso.
    const fallbackTimer = window.setTimeout(() => {
      if (cancelled || uplotRef.current) return;
      const w =
        el.clientWidth ||
        el.parentElement?.clientWidth ||
        el.parentElement?.parentElement?.clientWidth ||
        800;
      console.warn(`[uPlot/${id}] RO não disparou em 250ms, fallback w=${w}`);
      createOrResize(w, "fallback-timer");
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(fallbackTimer);
      ro.disconnect();
      uplotRef.current?.destroy();
      uplotRef.current = null;
    };
  }, [structureKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 2: atualiza DATA sem destruir o uPlot — roda toda vez que
  // timestamps ou values mudam (refetch periódico do TanStack). Isso
  // preserva sync de cursor, estado de zoom e elimina o bug do gráfico
  // sumindo no meio do uso por destruir+recriar mal.
  useEffect(() => {
    const u = uplotRef.current;
    if (!u) return;
    const id = `${syncKey || "?"}-${instanceIdRef.current}-${yLabel ?? ""}`;
    if (u.series.length !== series.length + 1) {
      console.debug(
        `[uPlot/${id}] skip setData: u.series=${u.series.length} esperado=${series.length + 1}`,
      );
      return;
    }
    try {
      u.setData([
        timestamps,
        ...series.map((s) => s.values as (number | null)[]),
      ]);
    } catch (err) {
      // uPlot tem race conditions internas que ocasionalmente lançam
      // "axis._found null" durante setData (especialmente com cursor sync
      // entre múltiplos charts). Não tem como prever — recuperamos
      // destruindo + recriando do zero via structureKey bump.
      console.error(`[uPlot/${id}] setData crashou, vou recriar:`, err);
      forceRecreate(`setData err: ${(err as Error)?.message ?? "unknown"}`);
    }
  }, [timestamps, series, syncKey, yLabel, forceRecreate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect 3: error listener global pra capturar erros assíncronos do uPlot
  // (chamados via microtask depois de setData). O try/catch no useEffect não
  // pega esses — chegam como uncaught no window.
  // Quando detecta "axis._found" ou similar, força recreate dessa instância.
  // Uma instância por painel observa; o filtro pelo stack inclui só erros
  // que vieram do nosso chart (mesmo container).
  useEffect(() => {
    const handler = (event: ErrorEvent) => {
      const msg = event.message || "";
      const stack = event.error?.stack || "";
      const isUplotErr =
        (msg.includes("not iterable") && stack.includes("uPlot")) ||
        stack.includes("drawAxesGrid") ||
        stack.includes("axesCalc");
      if (!isUplotErr) return;
      // Não dá pra saber QUAL instância crashou (uPlot não expõe), então
      // recriamos a nossa. Pior cenário: recria sem necessidade — barato.
      if (uplotRef.current) {
        forceRecreate("global error listener");
        event.preventDefault();
      }
    };
    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, [forceRecreate]);

  // Re-render por door events sem destruir o chart.
  // Guarda: só redraw se uPlot já passou pelo primeiro commit (tem data
  // populado E scales com min/max calculados). Chamar redraw antes pode
  // causar axis._found null em drawAxesGrid.
  useEffect(() => {
    const u = uplotRef.current;
    if (!u || !u.data || u.data.length < 1) return;
    // u.scales.x.min só é populado após o primeiro commit
    if (u.scales.x.min == null) return;
    try {
      u.redraw();
    } catch (err) {
      console.error("[uPlot/redraw door] err:", err);
    }
  }, [doorEvents]);

  // Container com dimensões explícitas — uPlot precisa de width/height >0
  // pra desenhar. Sem isso, em layouts flex/grid o div recém-anexado pode
  // ter 0×0 momentaneamente e o RO nunca dispara.
  return (
    <div
      ref={containerRef}
      className={className}
      style={{ minHeight: height, width: "100%" }}
    />
  );
}
